// Environment types for Mission Control integration

export type EnvironmentType = 'DevBox' | 'SIT' | 'UAT' | 'PROD';
export type AccessLevel = 'none' | 'read' | 'readwrite';

/** Raw environment record from the Mission Control API */
export interface MissionControlEnvironment {
  AADTenantId: string;
  EnvironmentId: string;
  ExpiresOn: string | null;
  FriendlyName: string;
  InstanceUrl: string;
  Kind: string;
  Type: EnvironmentType;
  WorkspaceCode: string;
}

/** Per-environment config stored for an AgentBox */
export interface EnvironmentConfig {
  environmentId: string;
  instanceUrl: string;
  friendlyName: string;
  type: EnvironmentType;
  accessLevel: AccessLevel;
  expiresOn?: string | null;
}

export interface ClaimDevBoxRequest {
  upn: string;
  projectCode: string;
  lifespan: string;
  description: string;
}

export interface ExtendResult {
  Result: 'Extended' | 'Blocked';
  ExpirationDate: string;
}

export const TENANT_ID = '67266d43-8de7-494d-9ed8-3d1bd3b3a764';

export const LIFESPAN_OPTIONS = [
  { key: '1', text: '1 day' },
  { key: '7', text: '1 week' },
  { key: '14', text: '2 weeks' },
  { key: '30', text: '1 month' },
] as const;

const DEFAULT_ACCESS: Record<EnvironmentType, AccessLevel> = {
  DevBox: 'readwrite',
  SIT: 'read',
  UAT: 'read',
  PROD: 'read',
};

/** Default access level for a given environment type */
export function getDefaultAccessLevel(type: EnvironmentType): AccessLevel {
  return DEFAULT_ACCESS[type];
}

/** Whether write access is allowed for this environment type (never for PROD) */
export function canWrite(type: EnvironmentType): boolean {
  return type !== 'PROD';
}

/** Convert a Mission Control environment record into a portal EnvironmentConfig */
export function toEnvironmentConfig(env: MissionControlEnvironment): EnvironmentConfig {
  return {
    environmentId: env.EnvironmentId,
    instanceUrl: env.InstanceUrl,
    friendlyName: env.FriendlyName,
    type: env.Type,
    accessLevel: getDefaultAccessLevel(env.Type),
    expiresOn: env.ExpiresOn,
  };
}

/** Group environments by type in display order */
export function groupEnvironmentsByType(envs: MissionControlEnvironment[]): Record<EnvironmentType, MissionControlEnvironment[]> {
  const groups: Record<EnvironmentType, MissionControlEnvironment[]> = {
    DevBox: [],
    SIT: [],
    UAT: [],
    PROD: [],
  };
  for (const env of envs) {
    if (groups[env.Type]) {
      groups[env.Type].push(env);
    }
  }
  return groups;
}

/** Display order for environment types */
export const ENV_TYPE_ORDER: EnvironmentType[] = ['DevBox', 'SIT', 'UAT', 'PROD'];

/** Human-friendly labels for access levels */
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  none: 'None',
  read: 'Read',
  readwrite: 'Read + Write',
};
